import { keyCodes } from '@wix/editor-elements-common-utils';
import type { KeyboardEvent, RefObject } from 'react';

export const isEnterOrSpace = (keyCode: number) =>
  keyCode === keyCodes.enter || keyCode === keyCodes.space;

export const isNavigationKey = (keyCode: number) =>
  keyCode === keyCodes.arrowDown ||
  keyCode === keyCodes.arrowUp ||
  keyCode === keyCodes.tab;

export const isEscape = (keyCode: number) => keyCode === keyCodes.escape;

export const isArrowDown = (keyCode: number) => keyCode === keyCodes.arrowDown;

export const isMovingBackward = (keyCode: number, shiftKey: boolean) =>
  keyCode === keyCodes.arrowUp || (keyCode === keyCodes.tab && shiftKey);

export const getNextIndex = (
  currentIndex: number,
  totalElements: number,
  movingBackward: boolean,
) => {
  if (movingBackward) {
    return currentIndex <= 0 ? totalElements - 1 : currentIndex - 1;
  }
  return currentIndex >= totalElements - 1 ? 0 : currentIndex + 1;
};

export const focusElementAtIndex = (
  elements: Array<HTMLElement>,
  index: number,
) => {
  elements[index]?.focus();
};

export const getCurrentElementIndex = (
  elements: Array<HTMLElement>,
  target: HTMLElement,
) => {
  return elements.indexOf(target);
};

export const queryFocusableMenuElements = (
  container: HTMLElement,
): Array<HTMLElement> => {
  const selector = 'a[href], button, [tabindex]:not([tabindex="-1"])';
  return Array.from(container.querySelectorAll(selector));
};

export const clickTarget = (event: KeyboardEvent) => {
  const target = event.target as HTMLElement;
  target.click();
};

export const handleMenuOpenRequest = (
  event: KeyboardEvent,
  onMenuOpen: () => void,
) => {
  event.preventDefault();
  onMenuOpen();
};

export const handleMenuCloseRequest = (
  event: KeyboardEvent,
  onMenuClose: () => void,
) => {
  event.preventDefault();
  onMenuClose();
};

export const handleArrowDownInMenu = (
  event: KeyboardEvent,
  menuRef: RefObject<{ focusFirstMenuItem: () => void }>,
) => {
  event.preventDefault();
  menuRef.current?.focusFirstMenuItem();
};

export const handleEscapeInMenu = (
  event: KeyboardEvent,
  onClose: () => void,
) => {
  event.preventDefault();
  onClose();
};

export const handleActivationInMenu = (event: KeyboardEvent) => {
  event.preventDefault();
  (event.target as HTMLElement).click();
};

export const handleArrowNavigation = (
  event: KeyboardEvent,
  containerRef: RefObject<HTMLElement>,
) => {
  if (isArrowDown(event.keyCode)) {
    event.stopPropagation();
    event.preventDefault();
  } else {
    event.preventDefault();
  }

  if (!containerRef.current) {
    return;
  }

  const focusableElements = queryFocusableMenuElements(containerRef.current);
  if (focusableElements.length === 0) {
    return;
  }

  const currentElement = event.target as HTMLElement;
  const currentIndex = getCurrentElementIndex(
    focusableElements,
    currentElement,
  );
  const movingBackward = isMovingBackward(event.keyCode, event.shiftKey);
  const nextIndex = getNextIndex(
    currentIndex,
    focusableElements.length,
    movingBackward,
  );

  focusElementAtIndex(focusableElements, nextIndex);
};
